import type {
  TaskMetadata,
  TaskMetadataCtaConfig,
  TaskMetadataDisplayCtx,
} from 'src/types/task-and-reminder';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  redirectToCta,
  useResolvedTaskCtas,
} from 'src/components/task-and-reminder/task-details/task-cta-buttons';

import {
  Card,
  CardActions,
  CardContent,
  CardFooter,
  CardHeader,
} from '@spaced-out/ui-design-system';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  BodySmall,
  BodySmallBold,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import css from './metadata-details.css';


export const MetadataDetails = ({metadata}: {metadata: TaskMetadata}) => {
  const {t} = useLabelTranslation();
  const allCtas = useResolvedTaskCtas(metadata);

  const displayEntries = metadata.display_config
    ? metadata.display_config
    : ([] as Array<TaskMetadataDisplayCtx>);
  const hasDisplayCtx = displayEntries.length > 0;

  if (allCtas.length === 0 && !hasDisplayCtx) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <SubTitleSmall color="secondary">
          {t('Task_Details', 'Task Details')}
        </SubTitleSmall>
      </CardHeader>
      <CardContent
        className={hasDisplayCtx ? css.displayContextGrid : undefined}
      >
        {hasDisplayCtx ? (
          displayEntries?.map?.(({label, value}: TaskMetadataDisplayCtx) => (
            <div className={css.displayContextItem} key={label}>
              <BodySmall color="secondary">{label}</BodySmall>
              <BodySmallBold>{value}</BodySmallBold>
            </div>
          ))
        ) : (
          <EmptyState
            title={t('No_task_details_present', 'No task details present')}
            imageVariant="data"
          />
        )}
      </CardContent>
      {allCtas.length > 0 ? (
        <CardFooter>
          <CardActions className={css.ctaActions}>
            <div className={css.ctaGrid}>
              {allCtas.map((cta: TaskMetadataCtaConfig, index: number) => (
                <Button
                  className={css.ctaButton}
                  key={cta.redirect_url}
                  type={index % 2 === 0 ? 'secondary' : 'primary'}
                  onClick={() => redirectToCta(cta.redirect_url)}
                  testId={generateTestId({
                    base: 'task-metadata-cta',
                    index,
                  })}
                >
                  {cta.button_label}
                </Button>
              ))}
            </div>
          </CardActions>
        </CardFooter>
      ) : null}
    </Card>
  );
};
